// src/pages/PixTransferPage.jsx
import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import accountService from '../services/accountService';
import pixService from '../services/pixService';
import Button from '../components/common/Button';
import Alert from '../components/common/Alert';
import PixStepIndicator from '../components/pix/PixStepIndicator';
import { ChevronLeft, UserRound } from 'lucide-react';
import { useT } from '../i18n';

const PIX_RECIPIENT_LIMIT = 6;

const DEFAULT_RECIPIENTS = [
  {
    name: 'Pedro Silva',
    pixKey: 'pedro.silva@dogbank.com',
    bank: 'BearBank',
    defaultAmount: 50,
  },
  {
    name: 'Eliane Oliveira',
    pixKey: 'eliane.oliveira@dogbank.com',
    bank: 'FoxBank',
    defaultAmount: 82.86,
  },
  {
    name: 'Renato Almeida',
    pixKey: 'renato.almeida@dogbank.com',
    bank: 'DOG BANK',
    defaultAmount: 100,
  },
];

const parseCurrencyInput = (value) => parseFloat(String(value).replace(/\./g, '').replace(',', '.'));

const formatAmountInput = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '';
  return numeric % 1 === 0
    ? String(numeric)
    : numeric.toFixed(2).replace('.', ',');
};

const loadSavedRecipients = (storageKey) => {
  try {
    const parsed = JSON.parse(localStorage.getItem(storageKey) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const saveRecipient = (storageKey, recipient) => {
  const current = loadSavedRecipients(storageKey);
  const next = [
    recipient,
    ...current.filter((item) => item.pixKey !== recipient.pixKey),
  ].slice(0, PIX_RECIPIENT_LIMIT);
  localStorage.setItem(storageKey, JSON.stringify(next));
  return next;
};

const PixTransferPage = () => {
  const { t } = useT();
  const { user } = useAuth();
  const navigate = useNavigate();

  /* ------------ STATE ------------ */
  const [pixKey, setPixKey] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');

  // erros & loading
  const [pixKeyError, setPixKeyError] = useState('');
  const [amountError, setAmountError] = useState('');
  const [generalError, setGeneralError] = useState('');
  const [loading] = useState(false);
  const [validatingKey, setValidatingKey] = useState(false);

  // dados auxiliares
  const [accountData, setAccountData] = useState(null);
  const [recipientInfo, setRecipientInfo] = useState(null);
  const [savedRecipients, setSavedRecipients] = useState([]);
  const availableBalance = accountData?.saldo ?? accountData?.balance ?? 0;

  const recipientMemoryKey = useMemo(() => {
    const cpf = user?.cpf || localStorage.getItem('cpf') || 'global';
    return `dogbank_pix_recipients_${cpf}`;
  }, [user]);

  const recommendedRecipients = useMemo(() => {
    const currentPixKey = user?.chavePix || localStorage.getItem('chavePix') || '';
    const byKey = new Map();

    [...savedRecipients, ...DEFAULT_RECIPIENTS].forEach((recipient) => {
      if (!recipient?.pixKey || recipient.pixKey === currentPixKey) return;
      if (!byKey.has(recipient.pixKey)) {
        byKey.set(recipient.pixKey, recipient);
      }
    });

    return Array.from(byKey.values()).slice(0, PIX_RECIPIENT_LIMIT);
  }, [savedRecipients, user]);

  useEffect(() => {
    setSavedRecipients(loadSavedRecipients(recipientMemoryKey));
  }, [recipientMemoryKey]);

  /* ------------ EFFECT: carregar saldo ------------ */
  useEffect(() => {
    const loadAccount = async () => {
      try {
        // Tenta pegar CPF do contexto ou do localStorage
        const cpf = user?.cpf || localStorage.getItem('cpf');
        if (cpf) {
          const data = await accountService.getAccountInfo(cpf);
          setAccountData(data);
        }
      } catch (err) {
        console.error(err);
        setGeneralError(t('pix.load_account_error_retry'));
      }
    };
    loadAccount();
  }, [user]);

  /* ------------ VALIDADORES ------------ */
  const validateAmount = () => {
    const numeric = parseCurrencyInput(amount);
    if (!amount.trim() || Number.isNaN(numeric) || numeric <= 0) {
      setAmountError(t('pix.enter_valid_amount'));
      return false;
    }
    if (accountData && numeric > availableBalance) {
      setAmountError(t('pix.insufficient_balance'));
      return false;
    }
    setAmountError('');
    return true;
  };

  const validatePixKey = async () => {
    if (!pixKey.trim()) {
      setPixKeyError(t('pix.enter_key'));
      return false;
    }
    try {
      setValidatingKey(true);
      const result = await pixService.validatePixKey(pixKey.trim());

      if (result.valid) {
        setRecipientInfo(result.user ?? { nome: t('pix.recipient'), banco: '' });
        setPixKeyError('');
        return true;
      }

      setPixKeyError(result.error || t('pix.invalid_key'));
      return false;
    } catch (err) {
      console.error(err);
      setPixKeyError(t('pix.key_validation_error'));
      return false;
    } finally {
      setValidatingKey(false);
    }
  };

  /* ------------ HANDLERS ------------ */
  const handleAmountChange = (e) => {
    const raw = e.target.value.replace(/[^\d,.]/g, '');
    const parts = raw.split(/[,.]/);
    if (parts.length === 1) {
      setAmount(parts[0]);
    } else {
      const integer = parts[0];
      const decimal = parts.slice(1).join('').slice(0, 2);
      setAmount(`${integer},${decimal}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError('');

    const amountOk = validateAmount();
    const keyOk = await validatePixKey();
    if (!amountOk || !keyOk) return;

    const numericAmount = parseCurrencyInput(amount);
    const normalizedPixKey = pixKey.trim();
    const remembered = saveRecipient(recipientMemoryKey, {
      name: recipientInfo?.nome || normalizedPixKey,
      pixKey: normalizedPixKey,
      bank: recipientInfo?.banco || '',
      defaultAmount: numericAmount,
      lastUsedAt: new Date().toISOString(),
    });
    setSavedRecipients(remembered);

    // ✅ CORREÇÃO: Usar rota relativa para funcionar dentro do MainLayout
    navigate('/dashboard/pix/confirm', {
      state: {
        pixKey: normalizedPixKey,
        amount: numericAmount.toFixed(2),
        description: description.trim(),
        receiverName: recipientInfo?.nome || '',
        receiverBank: recipientInfo?.banco || '',
        sourceAccountId: accountData?.id,
      },
    });
  };

  const handleQuickAmount = (value) => {
    setAmount(value.toString());
    setAmountError('');
  };

  const handleRecipientSuggestion = (recipient) => {
    setPixKey(recipient.pixKey);
    setRecipientInfo({
      nome: recipient.name,
      banco: recipient.bank || '',
    });
    if (recipient.defaultAmount) {
      setAmount(formatAmountInput(recipient.defaultAmount));
      setAmountError('');
    }
    setPixKeyError('');
  };

  /* ------------ UTILS ------------ */
  const formatCurrency = (val) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);

  const getPixKeyPlaceholder = () => {
    if (pixKey.includes('@')) return t('pix.placeholder_email');
    if (pixKey.match(/^\d/)) return t('pix.placeholder_cpf_phone');
    return t('pix.placeholder_key');
  };

  /* ------------ RENDER ------------ */
  return (
    <div className="max-w-2xl mx-auto animate-slide-up">
      {/* Header com voltar */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/dashboard')}
          className="w-10 h-10 rounded-full bg-white shadow-sm border border-slate-200 flex items-center justify-center text-slate-500 hover:text-purple-600 hover:border-purple-200 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">{t('pix.area_title')}</h1>
          <p className="text-sm text-slate-500">{t('pix.area_subtitle')}</p>
        </div>
      </div>

      <PixStepIndicator current={1} />

      {generalError && (
        <Alert
          type="error"
          message={generalError}
          onClose={() => setGeneralError('')}
          className="mb-6"
        />
      )}

      {/* Saldo disponível - Card destacado */}
      {accountData && (
        <div className="mb-8 p-6 bg-gradient-to-r from-purple-500 to-violet-600 rounded-2xl text-white shadow-lg shadow-purple-500/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">{t('pix.available_balance')}</p>
              <p className="text-2xl font-bold">
                {formatCurrency(availableBalance)}
              </p>
            </div>
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Chave PIX - Input moderno */}
        <div>
          <label className="block text-lg font-semibold text-neutral-900 mb-3">
            {t('pix.send_to_whom')}
          </label>
          {recommendedRecipients.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-neutral-700 mb-3">{t('pix.recommendations')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {recommendedRecipients.map((recipient) => (
                  <button
                    key={recipient.pixKey}
                    type="button"
                    onClick={() => handleRecipientSuggestion(recipient)}
                    className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left hover:border-purple-200 hover:bg-purple-50/60 transition-colors"
                    disabled={loading || validatingKey}
                  >
                    <span className="w-10 h-10 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center flex-shrink-0">
                      <UserRound className="w-5 h-5" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-bold text-slate-900 truncate">{recipient.name}</span>
                      <span className="block text-xs text-slate-500 truncate">{recipient.bank || recipient.pixKey}</span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="relative">
            <input
              type="text"
              value={pixKey}
              onChange={(e) => setPixKey(e.target.value)}
              placeholder={getPixKeyPlaceholder()}
              className={`w-full px-6 py-5 text-lg border-2 rounded-2xl bg-white focus:outline-none transition-all duration-200 ${
                pixKeyError 
                  ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100' 
                  : 'border-neutral-200 focus:border-primary-500 focus:ring-4 focus:ring-primary-100'
              }`}
              disabled={loading || validatingKey}
            />
            {validatingKey && (
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary-500 border-t-transparent"></div>
              </div>
            )}
          </div>
          
          {validatingKey && (
            <p className="text-sm text-primary-600 mt-3 flex items-center">
              <svg className="animate-spin mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {t('pix.validating_key')}
            </p>
          )}
          {pixKeyError && (
            <p className="text-sm text-red-600 mt-3 flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {pixKeyError}
            </p>
          )}

          {/* Info do destinatário - Card de sucesso */}
          {recipientInfo && !pixKeyError && (
            <div className="mt-4 p-4 bg-green-50 border-2 border-green-200 rounded-2xl">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-green-900">{recipientInfo.nome}</p>
                  {recipientInfo.banco && (
                    <p className="text-sm text-green-700">{recipientInfo.banco}</p>
                  )}
                  <p className="text-xs text-green-600 font-medium">{t('pix.valid_key')}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Valor - Input destacado */}
        <div>
          <label className="block text-lg font-semibold text-neutral-900 mb-3">
            {t('pix.how_much')}
          </label>
          <div className="relative">
            <div className="absolute left-6 top-1/2 transform -translate-y-1/2 text-2xl text-neutral-500 font-bold">
              R$
            </div>
            <input
              type="text"
              value={amount}
              onChange={handleAmountChange}
              placeholder="0,00"
              className={`w-full pl-20 pr-6 py-5 text-3xl font-bold border-2 rounded-2xl bg-white focus:outline-none transition-all duration-200 ${
                amountError 
                  ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100' 
                  : 'border-neutral-200 focus:border-primary-500 focus:ring-4 focus:ring-primary-100'
              }`}
              disabled={loading}
            />
          </div>
          {amountError && (
            <p className="text-sm text-red-600 mt-3 flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {amountError}
            </p>
          )}

          {/* Valores rápidos */}
          <div className="mt-4">
            <p className="text-sm font-medium text-neutral-700 mb-3">{t('pix.quick_amounts')}</p>
            <div className="flex gap-3 flex-wrap">
              {[10, 20, 50, 100, 200, 500].map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => handleQuickAmount(value)}
                  className="px-4 py-2 text-sm font-semibold border-2 border-neutral-200 rounded-xl hover:bg-primary-50 hover:border-primary-300 active:bg-primary-100 transition-all duration-200"
                >
                  R$ {value}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Descrição - Input clean */}
        <div>
          <label className="block text-lg font-semibold text-neutral-900 mb-3">
            {t('pix.description_optional')}
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('pix.description_placeholder')}
            className="w-full px-6 py-4 text-lg border-2 border-neutral-200 rounded-2xl bg-white focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all duration-200"
            disabled={loading}
          />
          <p className="text-sm text-neutral-500 mt-2">
            {t('pix.description_hint')}
          </p>
        </div>

        {/* Botão continuar - Destaque total */}
        <div className="pt-6">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            disabled={loading || validatingKey || !pixKey.trim() || !amount.trim()}
            className="py-5 text-xl font-bold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent mr-3"></div>
                {t('pix.processing')}
              </div>
            ) : (
              t('pix.continue')
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PixTransferPage;
